import { Button, Flex, FormStatus } from '@dnb/eufemia';
import { HelpProps } from '@dnb/eufemia/components/help-button/HelpButtonInline';
import { Form } from '@dnb/eufemia/extensions/forms';
import { close } from '@dnb/eufemia/icons';
import { JwtErrorCode } from '@portals/shared/common/utils';
import { ApiError } from '@portals/shared-frontend/utils/ApiError';
import { type JSX, type ReactNode, useCallback, useId, useState } from 'react';

import { addClientKey } from '@/api/app';
import LoadingModal from '@/components/LoadingModal';

import { JwtField } from '../../../CreateClient/components/JwtField';
import { JwtValidationResults } from '../../../CreateClient/components/JwtValidationResults';
import { CrqField } from '../../../CrqField';
import { useJwtEnvironmentMismatch } from '../../../useJwtEnvironmentMismatch';

type FormSchemaJson = {
  environment: string;
  jwks: string;
  crq?: string;
};

type Props = {
  appId: string;
  clientId: string;
  liveMode: boolean;
  isAdmin: boolean;
  canManageKeys: boolean;
  noPrivilegeError: ReactNode;
  onKeyAdded: () => Promise<void>;
  onCancel: () => void;
};

const crqHelp: HelpProps = {
  title: 'ServiceNow Identifier (BA/BSN)',
  content:
    'A ServiceNow change request (CRQ) in the "implement" state is required to add a signing key to a client in the live environment. Enter the ID for the relevant CRQ.',
};

export default function AddKeyForm({
  appId,
  clientId,
  liveMode,
  isAdmin,
  canManageKeys,
  noPrivilegeError,
  onKeyAdded,
  onCancel,
}: Props): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [jwtValidationErrors, setJwtValidationErrors] = useState<JwtErrorCode[]>([]);
  const [jwtToken, setJwtToken] = useState<string>('');

  const formId = `add-signing-key-form-${useId()}`;
  const { update } = Form.setData(formId);

  const handleJwtValidation = useCallback((errors: JwtErrorCode[], token: string) => {
    setJwtValidationErrors(errors);
    setJwtToken(token);
  }, []);

  const hasEnvironmentMismatch = useJwtEnvironmentMismatch({
    errors: jwtValidationErrors,
    liveMode,
    token: jwtToken,
  });

  const resetForm = useCallback(() => {
    setErrorMessage(null);
    update('/jwks', '');
    update('/crq', undefined);
    setJwtToken('');
    setJwtValidationErrors([]);
  }, [update]);

  const handleCancel = useCallback(() => {
    resetForm();
    onCancel();
  }, [onCancel, resetForm]);

  const onSubmit = useCallback(
    async ({ jwks, crq }: FormSchemaJson) => {
      try {
        setSubmitting(true);
        await addClientKey(appId, clientId, { jwks }, liveMode ? crq : undefined);
        resetForm();
        await onKeyAdded();
      } catch (error: unknown) {
        if (ApiError.isApiError(error)) {
          setErrorMessage(error.body.message);
        } else {
          throw error;
        }
      } finally {
        setSubmitting(false);
      }
    },
    [appId, clientId, liveMode, onKeyAdded, resetForm],
  );

  return (
    <Form.Handler<FormSchemaJson>
      defaultData={{
        environment: liveMode ? 'live' : 'test',
        jwks: '',
        crq: undefined,
      }}
      disabled={submitting || !canManageKeys}
      id={formId}
      locale="en-GB"
      onSubmit={onSubmit}
    >
      {submitting && <LoadingModal />}
      <Flex.Stack gap="small" wrap={false}>
        <Form.SubHeading>Add a signing key</Form.SubHeading>
        <JwtField formId={formId} onValidationChange={handleJwtValidation} />
        <JwtValidationResults
          errors={jwtValidationErrors}
          hasEnvironmentMismatch={hasEnvironmentMismatch}
          token={jwtToken}
        />
        {isAdmin && liveMode && <CrqField customHelp={crqHelp} />}
        {errorMessage && <FormStatus state="error" text={errorMessage} />}
        <Form.ButtonRow>
          <Form.SubmitButton
            disabled={!canManageKeys || jwtValidationErrors.length > 0 || hasEnvironmentMismatch}
            icon="save"
            iconPosition="left"
            text="Add key"
            tooltip={canManageKeys ? undefined : noPrivilegeError}
          />
          <Button icon={close} iconPosition="left" onClick={handleCancel} type="button" variant="tertiary">
            Cancel
          </Button>
        </Form.ButtonRow>
      </Flex.Stack>
    </Form.Handler>
  );
}
